import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronRight, ShieldCheck, ArrowRight } from "lucide-react";
import { KineticLines } from "@/components/Kinetic";
import { ComplianceRow } from "@/components/ComplianceRow";
import { BREADCRUMB_ROUTES } from "@/content/services";

export const PageHero = ({
  breadcrumb = [],
  pill,
  titleLines,
  description,
  primaryCta,
  secondaryCta,
  image,
  imageAlt = "",
  showCompliance = true,
  complianceItems,
  tall = false,
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.25]);

  return (
    <section
      ref={ref}
      className={`relative isolate flex items-center overflow-hidden ${
        tall ? "min-h-[88svh] lg:min-h-[92vh]" : "min-h-[78svh] lg:min-h-[78vh]"
      }`}
      data-testid="page-hero"
    >
      <motion.div className="absolute inset-0 -z-10" style={{ y: bgY }}>
        <img
          src={image}
          alt={imageAlt}
          width="1920"
          height="1080"
          fetchPriority="high"
          decoding="async"
          className="hero-photo h-[120%] w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-[rgba(11,15,26,0.90)] sm:bg-[rgba(11,15,26,0.86)] lg:bg-[rgba(11,15,26,0.42)]" />
      <div
        className="absolute inset-0 -z-10 hidden lg:block"
        style={{
          background:
            "linear-gradient(to right, rgba(11,15,26,0.95) 0%, rgba(11,15,26,0.68) 44%, rgba(11,15,26,0.22) 100%)",
        }}
      />
      <div
        className="absolute inset-0 -z-10"
        style={{
          background: "linear-gradient(to bottom, rgba(11,15,26,0.75) 0%, rgba(11,15,26,0.1) 45%, #0B0F1A 100%)",
        }}
      />
      <div
        className="glow-orb -left-40 top-1/3 h-[420px] w-[420px] bg-[#8E9AEA]/10"
        aria-hidden
      />

      <motion.div
        style={{ opacity: fade }}
        className="mx-auto w-full max-w-7xl px-5 pb-12 pt-28 sm:px-8 sm:pb-16 sm:pt-32 lg:px-16 lg:pt-36"
      >
        {breadcrumb.length > 0 && (
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-medium uppercase tracking-[0.16em] text-[#A6B0C9] sm:mb-8 sm:text-xs sm:tracking-[0.2em]"
            data-testid="hero-breadcrumb"
          >
            {breadcrumb.map((b, i) => {
              const isLast = i === breadcrumb.length - 1;
              const to = BREADCRUMB_ROUTES[b];
              const slug = b.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <React.Fragment key={b}>
                  {i > 0 && <ChevronRight className="h-3 w-3 text-[#A6B0C9]/60" />}
                  {!isLast && to ? (
                    <Link
                      to={to}
                      className="transition-colors duration-200 hover:text-white"
                      data-testid={`breadcrumb-link-${slug}`}
                    >
                      {b}
                    </Link>
                  ) : (
                    <span
                      className={isLast ? "text-[#DDE3F2]" : ""}
                      data-testid={`breadcrumb-current-${slug}`}
                    >
                      {b}
                    </span>
                  )}
                </React.Fragment>
              );
            })}
          </motion.nav>
        )}

        {pill && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <span className="pill-badge" data-testid="hero-eyebrow-pill">
              <ShieldCheck className="h-3.5 w-3.5 text-[#8E9AEA]" />
              {pill}
            </span>
          </motion.div>
        )}

        <KineticLines
          lines={titleLines}
          className="mt-6 max-w-4xl text-[2rem] font-extrabold leading-[1.1] tracking-tight text-white sm:mt-7 sm:text-[2.75rem] sm:leading-[1.08] md:text-5xl lg:text-[4.2rem] lg:leading-[1.06]"
        />

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-[#DDE3F2] sm:text-lg"
          data-testid="hero-description"
        >
          {description}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-8 cta-row"
        >
          {primaryCta && (
            <Link
              to={primaryCta.to}
              className="btn-primary"
              data-testid="hero-primary-cta"
            >
              {primaryCta.label}
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
          {secondaryCta && secondaryCta.to.startsWith("#") ? (
            <a
              href={secondaryCta.to}
              className="btn-secondary"
              data-testid="hero-secondary-cta"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(secondaryCta.to.slice(1))?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              {secondaryCta.label}
            </a>
          ) : (
            secondaryCta && (
              <Link to={secondaryCta.to} className="btn-secondary" data-testid="hero-secondary-cta">
                {secondaryCta.label}
              </Link>
            )
          )}
        </motion.div>

        {showCompliance && <ComplianceRow className="mt-10 sm:mt-14" items={complianceItems} />}
      </motion.div>
    </section>
  );
};
